/**
 * Workspace user directory for the Work Queue people pickers (CT-5094).
 *
 * Contract: user.yaml operationId `listUsersFilter` →
 *   POST /v1/workspace/user/list_filter
 * The tenant / workspace come from the token (never sent in the body). The
 * response body is intentionally minimal — `{ userUuid, fullName, photo }` per
 * item — which is exactly what a picker needs. Mirrors the proven beta Governance
 * Studio `getUserListFilters` (web-app-governance-studio/src/services/users.js),
 * which calls the **Platform** API (`api` → `VITE_API_BASE_URL`), not GS.
 *
 * `order_by` / `order_direction` are omitted so the backend default applies — the
 * spec (single camelCase field: createdAt|updatedAt|fullName) and the beta impl
 * (snake_case arrays) disagree, and omitting them sidesteps that ambiguity.
 */

import { platformApi } from './apiClient.js';
import { normalizePagedMeta, validateApiResponse } from '../shared/utils/apiResponse.js';

/** Map a workspace user (contract: { userUuid, fullName, photo }) to a picker option. */
function toUserOption(user = {}) {
  const id = user.userUuid ?? user.user_uuid ?? null;
  const name = user.fullName ?? user.full_name ?? id ?? '';
  return { id, name, photo: user.photo ?? null };
}

/**
 * Session cache for the unsearched first page — the directory every picker and
 * the inbox aggregate request identically (`{ pageSize: 100 }`). Without it,
 * opening Reassign three times fired three identical list_filter POSTs. Only the
 * unsearched page-1 call is cached; a search always hits the network. The
 * directory is effectively static per session; call {@link resetWorkspaceUsersCache}
 * after a mutation (or in tests) to force a refetch.
 */
let directoryCache = null;
let directoryInflight = null;

/** Clear the cached workspace directory (tests, or after a user mutation). */
export function resetWorkspaceUsersCache() {
  directoryCache = null;
  directoryInflight = null;
}

async function fetchWorkspaceUsers({ page, pageSize, keyword }) {
  const payload = { page, page_size: pageSize };
  // The validator only allows `full_name` as the search field today.
  if (keyword) payload.search = { field: 'full_name', keyword };

  const envelope = await platformApi.post('/v1/workspace/user/list_filter', payload);
  const { body, meta } = validateApiResponse(envelope, 'Failed to load users');
  const users = (Array.isArray(body) ? body : []).map(toUserOption).filter((u) => u.id);
  return { users, meta: normalizePagedMeta(meta) };
}

/**
 * List workspace users for a people picker (Reassign / Ask / My Team). The
 * unsearched first page is cached and de-duped across callers; a search bypasses
 * the cache and hits the network.
 *
 * @param {{ page?: number, pageSize?: number, search?: string|{keyword:string} }} params
 * @returns {Promise<{ users: Array<{id:string,name:string,photo:string|null}>, meta: Object|null }>}
 */
export async function listWorkspaceUsers({ page = 1, pageSize = 100, search } = {}) {
  const keyword = typeof search === 'string' ? search.trim() : search?.keyword?.trim();
  const cacheable = !keyword && page === 1;

  if (!cacheable) return fetchWorkspaceUsers({ page, pageSize, keyword });
  if (directoryCache) return directoryCache;
  if (directoryInflight) return directoryInflight;

  directoryInflight = fetchWorkspaceUsers({ page, pageSize, keyword });
  try {
    directoryCache = await directoryInflight;
    return directoryCache;
  } finally {
    directoryInflight = null;
  }
}

/**
 * Per-uuid resolution cache — a work-queue owner's uuid → picker option. Module-
 * level so repeated inbox fetches don't re-hit the network for the same person.
 * `null` is cached too (a uuid that failed / doesn't resolve) to avoid retry storms.
 */
const userByUuidCache = new Map();

/**
 * Resolve ONE user by uuid (CT-5094): `GET /v1/tenant/user/{uuid}` on the Platform
 * API (mirrors beta `getUserByUuid`). Used to resolve a known owner's name without
 * the 100-row cap of the picker's `list_filter`. Best-effort: returns `null` on any
 * failure so a missing name never breaks the inbox.
 *
 * @param {string} userUuid
 * @returns {Promise<{id:string,name:string,photo:string|null}|null>}
 */
export async function getUserByUuid(userUuid) {
  if (!userUuid) return null;
  if (userByUuidCache.has(userUuid)) return userByUuidCache.get(userUuid);
  try {
    const envelope = await platformApi.get(`/v1/tenant/user/${userUuid}`);
    const { body } = validateApiResponse(envelope, 'Failed to load user');
    const option = toUserOption({ ...body, userUuid: body?.userUuid ?? body?.user_uuid ?? userUuid });
    userByUuidCache.set(userUuid, option);
    return option;
  } catch {
    userByUuidCache.set(userUuid, null);
    return null;
  }
}

/** Max concurrent per-uuid GETs — bounds the resolution the way the fan-out is bounded. */
const RESOLVE_CONCURRENCY = 6;

/**
 * Resolve a set of owner uuids to a `{ [uuid]: fullName }` map (CT-5094). Dedupes
 * the input and fetches each distinct uuid once (cached), in **bounded** chunks so
 * an inbox with many distinct owners never bursts one request per row (mirrors the
 * fan-out's `allSettledPool` cap). A uuid that fails to resolve is simply omitted
 * (the owner chip falls back to the raw uuid).
 *
 * @param {Array<string|null|undefined>} uuids
 * @param {{ concurrency?: number }} [options]
 * @returns {Promise<Record<string,string>>}
 */
export async function resolveUserNames(uuids = [], { concurrency = RESOLVE_CONCURRENCY } = {}) {
  const distinct = [...new Set(uuids.filter(Boolean))];
  if (distinct.length === 0) return {};
  const nameById = {};
  for (let i = 0; i < distinct.length; i += concurrency) {
    const chunk = distinct.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop -- bounded chunks: one await per batch, not per uuid.
    const options = await Promise.all(chunk.map((uuid) => getUserByUuid(uuid)));
    chunk.forEach((uuid, j) => {
      const name = options[j]?.name;
      if (name) nameById[uuid] = name;
    });
  }
  return nameById;
}
